import type { CarConfigStateType } from "../redux/store";
import CarDataConsolidator from "../../../shared-logic/utils/classes/CarDataConsolidator";
import type { CategoryType, ExtraType, PackType } from "../../../../common-deprecated/types/CommonTypes";
import { EcoTagType, RequestStatusEnum } from "../../../../common-deprecated/types/CommonTypes";
import { formatPrice } from "../../../../common-deprecated/Globalize";
import type {
    ConfigFinanceType,
    ConfigPricingDetailsType,
    CtaButtonType,
    CtaCollectionType,
} from "./types/AceConfigType";
import {
    canNavigateToBuyStep,
    canNavigateToLeaseStep,
    canNavigateToReserveStep,
} from "../../../shared-logic/utils/checkoutUtils";
import type { FinanceRateStateType } from "../redux/slices/CarConfigFinanceSlice";
import { getEcoTagType } from "../../../shared-logic/utils/typedUtils";
import { showCarConfigMonthlyRate } from "./financeInsuranceRate";
import type {
    AvailableGradesForConfigurableGradeType,
    AvailableGradesForConfigurableType,
} from "../../../shared-logic/types/AvailableInOtherGradesType";
import { find } from "../../../../common-deprecated/utils";
import { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";

/**
 * Returns Extras filtered by business rules + search value + selected categories
 * @param state
 */
export const extrasSelector = (state: CarConfigStateType): ExtraType[] => {
    const { items, extraFilter, basket } = state;

    const wheelExtras = items.wheelExtras.filter((wheelExtra) => wheelExtra.category.code === "EXT/TYRES-WHEELS");

    let userFilteredExtras = [...items.extras].sort((a, b) => a.sortIndex - b.sortIndex);

    // Filter for search value when a search value is entered.
    if (extraFilter.searchValue) {
        userFilteredExtras = userFilteredExtras.filter(
            (extra) =>
                extra.name.toLowerCase().includes(extraFilter.searchValue.toLowerCase()) ||
                extra.description?.toLowerCase().includes(extraFilter.searchValue.toLowerCase()),
        );
    }

    // Filter the selected extras categories
    if (extraFilter.appliedCategoryFilters && extraFilter.appliedCategoryFilters.length) {
        userFilteredExtras = userFilteredExtras.filter((extra) => {
            return extraFilter.appliedCategoryFilters
                .map((filterExtrasCategory) => filterExtrasCategory.id)
                .includes(extra.category.id);
        });
    }

    // Filter the extras based on business rules
    const filteredExtras: ExtraType[] = userFilteredExtras
        .reduce((extrasArray: ExtraType[], extra) => {
            const showExtra =
                !wheelExtras.find((wheelExtra) => wheelExtra.id === extra.id) &&
                // Only show extras which are available for the current colour & upholstery combination.
                CarDataConsolidator.isAvailable(extra, basket.colour, basket.upholstery).available &&
                // Also check the colouring mode
                CarDataConsolidator.validatesForColourMode(extra, {
                    colour: basket.colour,
                    primaryAccentColour: basket.primaryAccentColour,
                    secondaryAccentColour: basket.secondaryAccentColour,
                }) &&
                // Don't show hidden extra's
                !CarDataConsolidator.hideExtra(extra, state.commonSettings) &&
                !extra.isCharger;

            if (showExtra) {
                return [...extrasArray, extra];
            }
            return extrasArray;
        }, [])
        .sort((a, b) => a.sortIndex - b.sortIndex);

    return filteredExtras;
};

/**
 * Returns all categories for the extras and packs
 * @param state
 */
export const getExtraCategoriesSelector = (state: CarConfigStateType): CategoryType[] => {
    const categories: CategoryType[] = state.items.extras.reduce((categoriesArray: CategoryType[], extra) => {
        if (!categoriesArray.find((category) => category.id === extra.category.id))
            return [...categoriesArray, extra.category];

        return categoriesArray;
    }, []);

    const packsCategories = state.items.packs.reduce(
        (packArray: CategoryType[], pack) =>
            pack.equipment.reduce((equipmentAcc: CategoryType[], equipmentItem) => {
                if (
                    !categories.find((category) => category.id === (equipmentItem as ExtraType).category.id) &&
                    !packArray.find((category) => category.id === (equipmentItem as ExtraType).category.id) &&
                    !equipmentAcc.find((category) => category.id === (equipmentItem as ExtraType).category.id)
                ) {
                    return [...equipmentAcc, (equipmentItem as ExtraType).category];
                }
                return equipmentAcc;
            }, packArray),
        [],
    );

    return categories.concat(packsCategories);
};

/**
 * Returns packs filtered through The selected extra categories + the filled in searchbar
 * @param state
 */
export const packsSelector = (state: CarConfigStateType): PackType[] => {
    const { items, extraFilter } = state;
    const { searchValue, appliedCategoryFilters } = extraFilter;

    // Check if the name contains elements of the search
    return items.packs
        .filter((pack) => {
            if (searchValue) {
                const hasFoundPack =
                    pack.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                    pack.description?.toLowerCase().includes(searchValue.toLowerCase());
                if (hasFoundPack) {
                    return true;
                }

                // Try and search for extras in pack also
                const foundEquipment = pack.equipment.find(
                    (equipment) =>
                        equipment.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                        equipment.description?.toLowerCase().includes(searchValue.toLowerCase()),
                );
                if (foundEquipment) {
                    return true;
                }

                return false;
            }

            return true;
        })
        .reduce((packsArray, pack) => {
            // If the user uses the search or has selected category filter
            if (appliedCategoryFilters.length > 0) {
                if (
                    pack.equipment.find(
                        (packEquipment) =>
                            !!appliedCategoryFilters.find(
                                (categoryFilter) => categoryFilter.id === (packEquipment as ExtraType).category.id,
                            ),
                    )
                ) {
                    return [...packsArray, pack];
                }
                return packsArray;
            }

            return [...packsArray, pack];
        }, [] as PackType[]);
};

/**
 * Returns The current step in the apheleia carconfig
 */
export const getCurrentNavQuerySelector = (
    state: CarConfigStateType,
): { step: CarConfigStep; modelId: string; carId: string } | null => {
    const { carConfigNavigation } = state;
    const { step, modelId, carId = "" } = carConfigNavigation;

    if (!Object.values(CarConfigStep).includes(step as CarConfigStep)) return null;

    return {
        step: step as CarConfigStep,
        modelId,
        carId,
    };
};

export const hasPacksOrExtrasSelector = (state: CarConfigStateType): boolean =>
    state.items.packs.length !== 0 || state.items.extras.length !== 0;

export const hasChargingSelector = (state: CarConfigStateType): boolean => {
    const ecoType = getEcoTagType(state.car.motorization.engine);
    const pluginOrElectric = ecoType === EcoTagType.Electric || ecoType === EcoTagType.Plugin;
    return (
        pluginOrElectric &&
        (!!state.carConfigSettings.charging?.length ||
            state.items.extras.filter((charger) => charger.isCharger && !charger.isHidden).length !== 0)
    );
};

/**
 * Check to see if the tax section needs to be activated
 * TFR can only show this when buy is enabled. Other countries can always show this.
 */
export const enableRegistrationTaxSelector = (state: CarConfigStateType): boolean => {
    const { commonSettings, carConfigSettings, externalPrice, car, finance, utils, basket } = state;
    return (
        (commonSettings.query.enableTaxAndCosts ||
            canNavigateToBuyStep<FinanceRateStateType>(
                car.availableForOnlineSales,
                carConfigSettings.allowedCheckoutFinanceProduct,
                finance,
                utils.financeOption,
                basket,
            ) ||
            commonSettings.country !== "fr") &&
        !!carConfigSettings.taxFormats?.length &&
        carConfigSettings.enableExternalPrice &&
        externalPrice.state !== RequestStatusEnum.Error
    );
};

type TaxLabelReturnType = {
    label: string;
    key: string;
    prices: string[];
    disclaimer?: string;
};

export const taxLabelsSelector = (state: CarConfigStateType): TaxLabelReturnType[] | null => {
    const { commonSettings, carConfigSettings, externalPrice } = state;

    if (
        externalPrice.calculatedPrice.extraCosts &&
        externalPrice.calculatedPrice.extraCosts?.length > 0 &&
        carConfigSettings.taxFormats
    ) {
        return carConfigSettings.taxFormats
            .map((tax) => {
                // Check if key exists in externalPrice
                const externalPriceTax = externalPrice.calculatedPrice.extraCosts?.find(
                    (extraCost) => extraCost.key === tax.key,
                );

                if (externalPriceTax) {
                    return {
                        label: tax.title,
                        key: tax.key,
                        disclaimer: tax.disclaimer,
                        prices: externalPriceTax.values.map((value) =>
                            formatPrice(value.amount, commonSettings.culture.name),
                        ),
                    };
                }
                return null;
            })
            .filter(Boolean) as TaxLabelReturnType[];
    } else return null;
};

export const ctaConfigSelector = (
    state: CarConfigStateType,
    key: keyof CtaCollectionType,
    isMobile: boolean,
): CtaButtonType[] => {
    const { ctaConfigs } = state.carConfigSettings;
    return (isMobile && ctaConfigs?.mobile[key]) || ctaConfigs?.default[key] || [];
};

export const summaryCtaSelector =
    (isMobile: boolean) =>
    (state: CarConfigStateType): CtaButtonType[] => {
        const buttons = ctaConfigSelector(state, "overviewCtas", isMobile);
        const { car, basket, carConfigSettings, finance, utils } = state;

        return (
            buttons
                // Remove go to buy button when step is not enabled
                .filter(
                    (button) =>
                        button.type !== "go-to-buy" ||
                        canNavigateToBuyStep<FinanceRateStateType>(
                            car.availableForOnlineSales,
                            carConfigSettings.allowedCheckoutFinanceProduct,
                            finance,
                            utils.financeOption,
                            basket,
                        ),
                )
                // Remove go to reserve button when step is not enabled
                .filter(
                    (button) =>
                        button.type !== "go-to-reserve" ||
                        canNavigateToReserveStep<FinanceRateStateType>(
                            car.availableForOnlineReservation,
                            carConfigSettings.allowedCheckoutReserveProduct,
                            finance,
                            utils.financeOption,
                        ),
                )
                // Remove go to lease button when step is not enabled
                .filter(
                    (button) =>
                        button.type !== "go-to-lease" ||
                        canNavigateToLeaseStep<FinanceRateStateType>(
                            car.availableForOnlineLeasing,
                            carConfigSettings.allowedCheckoutLeasingProduct,
                            finance,
                            utils.financeOption,
                        ),
                )
        );
    };

// A bit overkill to have a separate selector for this as showCarConfigMonthlyRate() could be called directly throughout components.
// But it's much cleaner to call this selector in components instead as it requires less separate properties to pass.
// showCarConfigMonthlyRate() can still be called directly if it's source doesn't allow hooks and doesn't have the full state.
export const showCarConfigMonthlyRateSelector = (state: CarConfigStateType): boolean => {
    const { commonSettings, car } = state;
    return showCarConfigMonthlyRate(commonSettings, car);
};

export const financeCtaSelector = (state: CarConfigStateType): ConfigFinanceType => {
    const { finance, commonSettings, basket, car, externalPrice, carConfigSettings } = state;
    const { total, info } = finance;
    const { enableExternalPrice } = carConfigSettings;
    const { model } = car;
    const showFinance = showCarConfigMonthlyRateSelector(state);
    return {
        showFinance,
        commonSettings,
        total,
        info,
        basket,
        modelCode: model.internalCode,
        // Calculate price (which updates the basket's total) is triggered after the external price has been fetched.
        // To prevent the price from visibly changing on the frontend, we make sure that the externalPrice is no longer fetching and has the ready state. Only then we are ready to show the price component.
        // If the externalPrice logic contains an error, we fallback to showing the original price.
        priceReadyToShow:
            !enableExternalPrice || (enableExternalPrice && externalPrice.state !== RequestStatusEnum.Error),
    };
};

export const pricingDetailsSelector = (state: CarConfigStateType): ConfigPricingDetailsType => {
    const {
        forceCashPrice,
        hideFinanceDetailsHeader,
        hideFinanceDetailsSummaryFooter,
        showFinanceDisclaimerInline,
        alwaysShowCashDisclaimer,
    } = state.carConfigSettings;
    return {
        forceCashPrice,
        hideFinanceDetailsHeader,
        hideFinanceDetailsSummaryFooter,
        showFinanceDisclaimerInline,
        alwaysShowCashDisclaimer,
    };
};

export const availableGradesForConfigurableSelector = (
    state: CarConfigStateType,
): {
    availableGradesForConfigurable: AvailableGradesForConfigurableType | undefined;
    selectedGrade: AvailableGradesForConfigurableGradeType | undefined;
    fetching: boolean;
} => {
    const { availableGradesForConfigurable } = state.availableInOtherGrades;

    if (availableGradesForConfigurable?.fetching === false && availableGradesForConfigurable.error === false) {
        if (availableGradesForConfigurable?.selectedGradeId) {
            const selectedGrade = find(
                availableGradesForConfigurable.selectedGradeId,
                availableGradesForConfigurable?.grades,
            );

            return {
                availableGradesForConfigurable,
                selectedGrade,
                fetching: availableGradesForConfigurable.fetching,
            };
        }
    }

    return { availableGradesForConfigurable: undefined, selectedGrade: undefined, fetching: true };
};
